import React, { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { semAcento } from '../utils/texto';

// =====================================================================
// Ordenação de tabela por clique no cabeçalho — compartilhada pelo portal.
//
// ⚠️ Nasceu no Compras › Solicitações (2026-08) e foi EXTRAÍDA para cá quando
//    Catálogo e Reparos pediram o mesmo (DEM-00072, 2026-11): a lógica de sort
//    é LÓGICA, e três cópias dela dariam três bugs diferentes.
//
// Três peças, acopladas só pelo necessário:
//   - ordenarLista: a função PURA (testada) que ordena de fato;
//   - useOrdenacaoTabela: o hook que guarda o estado e memoiza o resultado;
//   - ThOrd: o <th> clicável com a seta.
//
// Cada tela passa seu `valorDe(item, campo)` para os casos especiais (número,
// data, status por fluxo); quem não passa cai no default (texto sem acento).
// =====================================================================

// Extrator de valor padrão: o texto da coluna, sem acento (ordena igual à busca).
const valorPadrao = (item, campo) => semAcento(item[campo]);

/**
 * Ordena `lista` por `ordem` ({campo, dir}) usando `valorDe(item, campo)`.
 * PURA e sem efeito colateral.
 *
 * ⚠️ `ordem` nula (ou sem campo) devolve a MESMA referência da lista — é a "ordem
 *    natural" que veio do servidor (3º clique do cabeçalho volta a ela).
 * ⚠️ Vazio (null/undefined/'') vai SEMPRE para o fim, nos dois sentidos: o que
 *    falta preencher é ausente, não "o menor" — senão o desc jogaria os vazios no topo.
 * ⚠️ Número compara por subtração; o resto por localeCompare('pt-BR') (trata acento/ç).
 *    Datas em ISO (AAAA-MM-DD) comparam cronologicamente já como texto.
 */
export function ordenarLista(lista, ordem, valorDe = valorPadrao) {
  if (!ordem || !ordem.campo) return lista;
  const dir = ordem.dir === 'desc' ? -1 : 1;
  return [...lista].sort((a, b) => {
    const va = valorDe(a, ordem.campo);
    const vb = valorDe(b, ordem.campo);
    const aVazio = va === null || va === undefined || va === '';
    const bVazio = vb === null || vb === undefined || vb === '';
    if (aVazio && bVazio) return 0;
    if (aVazio) return 1;
    if (bVazio) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb), 'pt-BR') * dir;
  });
}

/**
 * Estado + ações da ordenação de UMA tabela; devolve a lista já ordenada.
 * `alternarOrdem(campo)`: 3 cliques — asc → desc → volta à ordem natural.
 */
export function useOrdenacaoTabela(lista, valorDe = valorPadrao) {
  const [ordem, setOrdem] = useState(null); // { campo, dir } | null
  const alternarOrdem = (campo) => setOrdem((o) => {
    if (o?.campo !== campo) return { campo, dir: 'asc' };
    if (o.dir === 'asc') return { campo, dir: 'desc' };
    return null; // 3º clique: ordem natural (a do servidor)
  });
  const ordenadas = useMemo(
    () => ordenarLista(lista, ordem, valorDe),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valorDe é estável (função pura declarada no módulo da tela); incluí-la só recriaria o memo à toa
    [lista, ordem],
  );
  return { ordem, alternarOrdem, ordenadas };
}

/**
 * Cabeçalho clicável. `estilo` = o thStyle da tela (já no padrão cinza do portal);
 * a seta herda a cor do texto (lucide usa currentColor). Em coluna numérica
 * (`align='right'`) a seta fica à esquerda do rótulo, do lado para onde o olho corre.
 */
export function ThOrd({ campo, children, align = 'left', ordem, alternarOrdem, estilo }) {
  const ativa = ordem?.campo === campo;
  const Icone = !ativa ? ArrowUpDown : (ordem.dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th style={{ ...estilo, textAlign: align, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => alternarOrdem(campo)}
      title={`Ordenar por ${String(children)}`}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
        {children}
        <Icone size={13} style={{ opacity: ativa ? 1 : 0.45 }} />
      </span>
    </th>
  );
}
